import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './HighlightsForm.css';

export default function AdmissionsRequirementsForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({ title: '', subtitle: '', description: '', requirements: [] });
  const [pendingFields, setPendingFields] = useState({ title: false, subtitle: false, description: false });
  const [pendingRequirements, setPendingRequirements] = useState({});
  const [originalData, setOriginalData] = useState({...formData});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [selectedRequirementForModal, setSelectedRequirementForModal] = useState(null);
  const [tempTag, setTempTag] = useState({});

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/admissions/cms/sections`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error(response.status);
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const section = result.data.find(s => s.section_type === 'admissions-requirements');
        if (section) {
          setSectionId(section.id);
          let content = section.content_published;
          if (typeof content === 'string') content = JSON.parse(content);
          const en = content?.en || {};
          const newData = {
            title: en.title || '',
            subtitle: en.subtitle || '',
            description: en.description || '',
            requirements: Array.isArray(en.requirements) ? en.requirements : []
          };
          setFormData(newData);
          setOriginalData(newData);

          const pRes = await fetch(`${apiBase}/cms/section/${section.id}/with-pending`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          });
          if (pRes.ok) {
            const pResult = await pRes.json();
            if (pResult.success && pResult.data?.pending_changes) {
              const pMap = { title: false, subtitle: false, description: false };
              const rMap = {};
              pResult.data.pending_changes.forEach(c => {
                if (c.status === 'pending' && c.locale === 'en') {
                  if (['title', 'subtitle', 'description'].includes(c.field_id)) pMap[c.field_id] = true;
                  else if (c.field_id?.includes('requirements.')) {
                    const m = c.field_id.match(/requirements\.(\d+)\./);
                    if (m) rMap[`req_${m[1]}`] = true;
                  }
                }
              });
              setPendingFields(pMap);
              setPendingRequirements(rMap);
            }
          }
        }
      }
    } catch (err) {
      console.error('[AdmissionsRequirementsForm] Load error:', err.message);
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (name, value) => setFormData(prev => ({...prev, [name]: value}));
  
  const handleRequirementChange = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.map((r, i) => i === idx ? {...r, [field]: value} : r)
    }));
  };

  const handleAddTag = (idx, type, value) => {
    if (!value.trim()) return;
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.map((r, i) => i === idx ? {...r, [type]: [...(r[type] || []), value.trim()]} : r)
    }));
    setTempTag(p => ({...p, [`${idx}-${type}`]: ''}));
  };

  const handleRemoveTag = (idx, type, tagIdx) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.map((r, i) => i === idx ? {...r, [type]: r[type].filter((_, ti) => ti !== tagIdx)} : r)
    }));
  };

  const handleSubmitField = async (name) => {
    if (!sectionId) { toast.error('No section'); return; }
    if (!formData[name]) { toast.warning(`Fill ${name}`); return; }
    if (formData[name] === originalData[name]) { toast.warning('No changes'); return; }

    setSubmitting(p => ({...p, [name]: true}));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: name,
          field_name: name,
          old_value: originalData[name],
          new_value: formData[name],
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || res.ok) {
        toast.success('Submitted');
        setPendingFields(p => ({...p, [name]: true}));
        setOriginalData(p => ({...p, [name]: formData[name]}));
      } else toast.error(result.error || 'Failed');
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({...p, [name]: false}));
    }
  };

  const handleSubmitRequirement = async (idx) => {
    if (!sectionId) { toast.error('No section'); return; }
    const req = formData.requirements[idx];
    if (!req?.level) { toast.warning('Fill level'); return; }

    const key = `req_${idx}`;
    setSubmitting(p => ({...p, [key]: true}));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `requirements.${idx}.update`,
          field_name: `Requirement: ${req.level}`,
          old_value: originalData.requirements[idx] || {},
          new_value: req,
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || res.ok) {
        toast.success('Requirement submitted');
        setPendingRequirements(p => ({...p, [key]: true}));
        setOriginalData(p => ({...p, requirements: p.requirements.map((r, i) => i === idx ? req : r)}));
      } else toast.error(result.error || 'Failed');
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({...p, [key]: false}));
    }
  };

  const handleDeleteRequirement = (idx) => {
    if (window.confirm('Delete requirement?')) {
      setFormData(prev => ({...prev, requirements: prev.requirements.filter((_, i) => i !== idx)}));
      setPendingRequirements(p => { const u = {...p}; delete u[`req_${idx}`]; return u; });
    }
  };

  const handleAddRequirement = () => {
    setFormData(prev => ({
      ...prev,
      requirements: [...prev.requirements, { level: '', description: '', documents: [], eligibility: [] }]
    }));
  };

  if (loading) return <div className="hero-form-wrapper"><div className="loading">Loading...</div></div>;

  return (
    <div className="hero-form-wrapper">
      <ToastContainer />
      <div className="hero-form-header">
        <h1 className="hero-form-title">Admissions Requirements</h1>
        <p className="hero-form-description">Edit title, subtitle, description & add requirements for each level</p>
      </div>

      {/* Title */}
      <div className="form-section">
        <div className="field-header">
          <label>Title</label>
          {pendingFields.title && <span className="status-badge pending">Pending</span>}
        </div>
        <input type="text" value={formData.title} onChange={e => handleFieldChange('title', e.target.value)} disabled={submitting.title || pendingFields.title} placeholder="Section title..." className="text-input" />
        <button onClick={() => handleSubmitField('title')} disabled={submitting.title || pendingFields.title || formData.title === originalData.title} className="submit-btn">
          {submitting.title ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Subtitle */}
      <div className="form-section">
        <div className="field-header">
          <label>Subtitle</label>
          {pendingFields.subtitle && <span className="status-badge pending">Pending</span>}
        </div>
        <input type="text" value={formData.subtitle} onChange={e => handleFieldChange('subtitle', e.target.value)} disabled={submitting.subtitle || pendingFields.subtitle} placeholder="Subtitle..." className="text-input" />
        <button onClick={() => handleSubmitField('subtitle')} disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle} className="submit-btn">
          {submitting.subtitle ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Description */}
      <div className="form-section">
        <div className="field-header">
          <label>Description</label>
          {pendingFields.description && <span className="status-badge pending">Pending</span>}
        </div>
        <textarea value={formData.description} onChange={e => handleFieldChange('description', e.target.value)} disabled={submitting.description || pendingFields.description} placeholder="Description..." className="textarea-input" rows="3" />
        <button onClick={() => handleSubmitField('description')} disabled={submitting.description || pendingFields.description || formData.description === originalData.description} className="submit-btn">
          {submitting.description ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Requirements */}
      <div className="form-section">
        <div className="items-section-header">
          <h2 className="form-section-title">Requirements by Level</h2>
          <button type="button" onClick={handleAddRequirement} className="btn-add-item">+ Add Requirement</button>
        </div>

        {formData.requirements.length === 0 ? (
          <div className="items-empty-state"><p>No requirements. Click "Add Requirement"</p></div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1.5rem' }}>
            {formData.requirements.map((req, idx) => {
              const key = `req_${idx}`;
              const isPending = pendingRequirements[key];
              return (
                <div key={idx} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  {/* Compact Card Content */}
                  <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1 }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#1f2937', lineHeight: 1.3 }}>
                      {req.level || 'Untitled'}
                    </h3>
                    {req.description && (
                      <p style={{ margin: 0, fontSize: '0.875rem', color: '#4b5563', lineHeight: 1.5 }}>
                        {req.description.substring(0, 80)}...
                      </p>
                    )}
                    {req.documents && req.documents.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {req.documents.slice(0, 2).map((doc, di) => (
                          <span key={di} style={{ fontSize: '0.75rem', background: '#e0e7ff', color: '#4f46e5', padding: '0.25rem 0.5rem', borderRadius: '3px' }}>
                            {doc.substring(0, 12)}
                          </span>
                        ))}
                        {req.documents.length > 2 && (
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>+{req.documents.length - 2}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Card Actions */}
                  <div style={{ padding: '0.75rem', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '0.5rem' }}>
                    <button 
                      type="button"
                      onClick={() => setSelectedRequirementForModal(idx)}
                      disabled={isPending}
                      style={{ flex: 1, padding: '0.5rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
                    >
                      Edit
                    </button>
                    <button 
                      type="button"
                      onClick={() => handleDeleteRequirement(idx)}
                      disabled={isPending}
                      style={{ flex: 1, padding: '0.5rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
                    >
                      Delete
                    </button>
                  </div>

                  {isPending && <div style={{ padding: '0.5rem', background: '#fef08a', color: '#92400e', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600' }}>Pending Approval</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Requirement Modal */}
      {selectedRequirementForModal !== null && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }} onClick={() => setSelectedRequirementForModal(null)}>
          <div style={{ background: 'white', borderRadius: '12px', maxHeight: '90vh', overflow: 'auto', maxWidth: '700px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>Edit Requirement</h2>
              <button type="button" onClick={() => setSelectedRequirementForModal(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Level */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Level *</label>
                <input 
                  type="text" 
                  value={formData.requirements[selectedRequirementForModal]?.level || ''} 
                  onChange={e => handleRequirementChange(selectedRequirementForModal, 'level', e.target.value)} 
                  placeholder="e.g., Preschool & Kindergarten" 
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Description */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Description</label>
                <textarea 
                  value={formData.requirements[selectedRequirementForModal]?.description || ''} 
                  onChange={e => handleRequirementChange(selectedRequirementForModal, 'description', e.target.value)} 
                  placeholder="Requirements description..." 
                  rows="3"
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Documents */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Documents</label>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input 
                    type="text" 
                    value={tempTag[`${selectedRequirementForModal}-documents`] || ''} 
                    onChange={e => setTempTag(p => ({...p, [`${selectedRequirementForModal}-documents`]: e.target.value}))}
                    placeholder="Add document..."
                    style={{ flex: 1, padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                  />
                  <button 
                    type="button" 
                    onClick={() => handleAddTag(selectedRequirementForModal, 'documents', tempTag[`${selectedRequirementForModal}-documents`] || '')}
                    style={{ padding: '0.75rem 1.5rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Add
                  </button>
                </div>
                {formData.requirements[selectedRequirementForModal]?.documents && formData.requirements[selectedRequirementForModal].documents.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {formData.requirements[selectedRequirementForModal].documents.map((doc, di) => (
                      <div key={di} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#e0e7ff', color: '#4f46e5', padding: '0.5rem 0.75rem', borderRadius: '4px', fontSize: '0.875rem' }}>
                        <span>{doc}</span>
                        <button 
                          type="button" 
                          onClick={() => handleRemoveTag(selectedRequirementForModal, 'documents', di)}
                          style={{ background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Eligibility */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Eligibility</label>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input 
                    type="text" 
                    value={tempTag[`${selectedRequirementForModal}-eligibility`] || ''} 
                    onChange={e => setTempTag(p => ({...p, [`${selectedRequirementForModal}-eligibility`]: e.target.value}))}
                    placeholder="Add eligibility..."
                    style={{ flex: 1, padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                  />
                  <button 
                    type="button" 
                    onClick={() => handleAddTag(selectedRequirementForModal, 'eligibility', tempTag[`${selectedRequirementForModal}-eligibility`] || '')}
                    style={{ padding: '0.75rem 1.5rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Add
                  </button>
                </div>
                {formData.requirements[selectedRequirementForModal]?.eligibility && formData.requirements[selectedRequirementForModal].eligibility.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {formData.requirements[selectedRequirementForModal].eligibility.map((elig, ei) => (
                      <div key={ei} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#dbeafe', color: '#1e40af', padding: '0.5rem 0.75rem', borderRadius: '4px', fontSize: '0.875rem' }}>
                        <span>{elig}</span>
                        <button 
                          type="button" 
                          onClick={() => handleRemoveTag(selectedRequirementForModal, 'eligibility', ei)}
                          style={{ background: 'none', border: 'none', color: '#1e40af', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div style={{ display: 'flex', gap: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                <button 
                  type="button"
                  onClick={() => handleSubmitRequirement(selectedRequirementForModal)}
                  disabled={submitting[`req_${selectedRequirementForModal}`]}
                  style={{ flex: 1, padding: '0.75rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  {submitting[`req_${selectedRequirementForModal}`] ? 'Submitting...' : 'Submit'}
                </button>
                <button 
                  type="button"
                  onClick={() => setSelectedRequirementForModal(null)}
                  style={{ flex: 1, padding: '0.75rem', background: '#6b7280', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
